import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function IdentitySection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const features = [
    { titleKey: "identity.feature1.title", descKey: "identity.feature1.desc", icon: "ri-shield-check-line" },
    { titleKey: "identity.feature2.title", descKey: "identity.feature2.desc", icon: "ri-speed-line" },
    { titleKey: "identity.feature3.title", descKey: "identity.feature3.desc", icon: "ri-global-line" },
  ];

  return (
    <section
      id="identity"
      ref={sectionRef}
      className="bg-ax-light py-20 md:py-28"
    >
      <div className="px-6 md:px-12 lg:px-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left: Image */}
          <div className="lg:w-2/5 animate-in opacity-0 translate-y-6 transition-all duration-700">
            <div className="relative">
              <span className="inline-block border border-ax-dark/20 rounded-full px-4 py-1 text-xs font-medium text-ax-dark/60 mb-6 tracking-wide">
                {t("identity.label")}
              </span>
              <div className="rounded-2xl overflow-hidden bg-white border border-ax-silver-light">
                <img
                  src="https://static.readdy.ai/image/1c927f723c274043d230f8a1015ea0aa/36d649847642a5cfe5c2b94680a4b052.png"
                  alt="AX NAS Device"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="lg:w-3/5 flex flex-col justify-center">
            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-200">
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ax-dark font-bold leading-tight mb-8">
                <span className="block">{t("identity.companyName")}</span>
              </h2>
            </div>

            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-400">
              <p className="text-ax-dark/60 text-base md:text-lg leading-relaxed mb-6">
                {t("identity.vision")}
              </p>
              <p className="text-ax-dark/50 text-sm md:text-base leading-relaxed mb-8">
                {t("identity.description")}
              </p>
            </div>

            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-600 flex flex-wrap gap-6 mb-8">
              {features.map((f) => (
                <div key={f.titleKey} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-ax-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${f.icon} text-ax-red`} />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-ax-dark">{t(f.titleKey)}</p>
                    <p className="text-xs text-ax-dark/50 mt-1">{t(f.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-800">
              <button
                onClick={() => navigate("/story")}
                className="bg-ax-dark text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-ax-dark-alt transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("identity.cta")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}